import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowRight,
  Cloud,
  User,
  Bell,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { useDisplayableApplications, useCurrentOrganization } from "@/hooks/api";
import { useApiToken } from "@/components/providers/token-provider";
import { trackRumAction } from "@/lib/datadog";

export default function HomePage() {
  const { user } = useApiToken();
  const { data: org } = useCurrentOrganization();
  const [mounted, setMounted] = useState(false);

  const [isSignedIn, setIsSignedIn] = useState(true);
  const userName = user?.first_name
    ? `${user.first_name} ${user.last_name}`
    : "User";
  const orgInitial = org?.name?.trim()?.charAt(0)?.toUpperCase() || "?";

  // Fetch applications from the API
  const {
    data: availableApplications,
    loading: applicationsLoading,
    error: applicationsError,
    refetch: refetchApplications,
  } = useDisplayableApplications();

  useEffect(() => {
    setMounted(true);
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
      return "Good morning";
    } else if (hour >= 12 && hour < 17) {
      return "Good afternoon";
    } else {
      return "Good evening";
    }
  };

  if (!isSignedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted/20">
        <div className="container mx-auto px-4 py-24">
          <div className="max-w-5xl mx-auto text-center">
            <div className="mb-20">
              {mounted && (
                <div className="mb-12 relative inline-block">
                  <div className="absolute inset-0 bg-primary/5 rounded-3xl blur-2xl"></div>
                  <div className="relative p-6 bg-background/80 rounded-3xl border shadow-lg">
                    <img
                      src="/logos/urbint/Urbint Wordmark Black target side.svg"
                      alt="StormImpact Logo"
                      className="h-auto w-full max-w-[400px] opacity-90"
                    />
                  </div>
                </div>
              )}

              <h1 className="mb-8 text-4xl font-bold leading-tight tracking-tight text-foreground sm:text-5xl lg:text-6xl">
                Data-driven insights for
                <br />
                <span className="bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
                  utility resilience
                </span>
              </h1>

              <p className="text-xl text-muted-foreground mb-12 max-w-3xl mx-auto leading-relaxed font-medium">
                Advanced analytics and predictive modeling to keep the utility
                grid running during severe weather events.
              </p>

              <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
                <Button
                  size="lg"
                  className="text-lg px-10 py-4 h-16 shadow-lg hover:shadow-xl transition-all duration-300 font-semibold"
                  onClick={() => setIsSignedIn(true)}
                >
                  Get Started <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Authenticated user view
  return (
    <div className="flex min-h-[calc(100vh-4rem)] flex-col overflow-x-hidden bg-background pt-8 sm:pt-14">
    <div className="container mx-auto flex flex-1 flex-col px-4">
      <div className="mx-auto flex w-full max-w-7xl flex-1 flex-col">
          {/* Welcome Header */}
          <div className="mb-6 flex-shrink-0 text-center sm:mb-8">
            <div className="mb-6 flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-6">
              {mounted && (
                <>
                  {org?.logo_url ? (
                    <div className="relative shrink-0">
                      <div className="absolute inset-0 bg-primary/5 rounded-2xl blur-xl"></div>
                      <div className="relative p-3 bg-background/80 rounded-2xl border shadow-sm">
                        <img
                          src={org.logo_url}
                          alt={org.name ? `${org.name} logo` : "Organization logo"}
                          className="h-20 w-20 object-contain opacity-90 sm:h-24 sm:w-24"
                        />
                      </div>
                    </div>
                  ) : org && (
                    <div className="relative shrink-0">
                      <div className="absolute inset-0 bg-primary/5 rounded-2xl blur-xl"></div>
                      <div className="relative flex h-20 w-20 items-center justify-center rounded-2xl border bg-background/80 p-3 shadow-sm sm:h-24 sm:w-24">
                        <span className="text-5xl font-bold leading-none text-primary/80 sm:text-7xl">
                          {orgInitial}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}
              <div className="min-w-0">
                <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
                  {getGreeting()},{" "}
                  <span className="break-words text-primary">{userName || "User"}</span>
                </h1>
                <p className="mt-2 text-base font-medium text-muted-foreground sm:text-lg">
                  Ready to dive into your applications
                </p>
              </div>
            </div>
          </div>

          {/* Applications */}
          <div className="relative flex-1 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/3 to-secondary/3 rounded-3xl"></div>
            <div className="relative rounded-2xl border bg-background/60 p-4 shadow-lg backdrop-blur-sm sm:rounded-3xl sm:p-6 lg:p-8">
              <div className="mb-6 text-center sm:mb-10">
                <div className="inline-flex items-center gap-3 mb-4">
                  <h2 className="text-2xl font-bold sm:text-3xl">Your Applications</h2>
                </div>
                <p className="mx-auto max-w-2xl text-base font-medium text-muted-foreground sm:text-lg">
                  Access powerful predictive tools designed to enhance utility
                  grid resilience
                </p>
              </div>

              {/* Loading State */}
              {applicationsLoading && (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
                  {[1, 2, 3].map((i) => (
                    <Card key={i} className="relative overflow-hidden h-full flex flex-col bg-background border-2">
                      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-muted to-muted/60"></div>
                      <CardHeader className="pb-4 pt-6 flex-grow flex flex-col">
                        <div className="flex items-center justify-between mb-3 min-h-[3.5rem]">
                          <Skeleton className="h-6 w-44" />
                          <Skeleton className="h-5 w-20 ml-16 flex-shrink-0" />
                        </div>
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-3/4 mt-2" />
                      </CardHeader>
                      <CardContent className="pt-0 mt-auto">
                        <Skeleton className="h-10 w-full rounded-md" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Error State */}
              {applicationsError && !applicationsLoading && (
                <Card className="text-center py-16 bg-background border-2 border-destructive/20">
                  <CardContent>
                    <div className="w-16 h-16 mx-auto mb-6 bg-destructive/10 rounded-full flex items-center justify-center">
                      <AlertCircle className="h-8 w-8 text-destructive" />
                    </div>
                    <p className="text-lg text-foreground font-medium mb-2">
                      Failed to load applications
                    </p>
                    <p className="text-sm text-muted-foreground mb-6">
                      {applicationsError.message}
                    </p>
                    <Button onClick={() => refetchApplications()} variant="outline">
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Try Again
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Applications List */}
              {!applicationsLoading &&
                !applicationsError &&
                availableApplications &&
                availableApplications.length > 0 && (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
                    {availableApplications.map((app) => {
                      const isEnabled = app.enabled;

                      const cardContent = (
                        <Card
                          className={`group relative overflow-hidden transition-all duration-500 h-full flex flex-col ${
                            isEnabled
                              ? "hover:shadow-2xl cursor-pointer bg-background border-2 hover:border-primary/20 hover:-translate-y-1"
                              : "cursor-not-allowed opacity-50 bg-muted/20 border-2 border-dashed border-muted-foreground/20"
                          }`}
                        >
                          <div
                            className={`absolute top-0 left-0 w-full h-1 ${
                              isEnabled
                                ? "bg-gradient-to-r from-primary to-secondary"
                                : "bg-gradient-to-r from-muted-foreground/30 to-muted-foreground/20"
                            }`}
                          ></div>
                          <CardHeader className="pb-4 pt-6 flex-grow flex flex-col">
                            <div className="mb-4 flex min-h-[3.5rem] min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                              <CardTitle
                                className={`min-w-0 break-words text-xl font-bold leading-tight ${
                                  isEnabled ? "" : "text-muted-foreground"
                                }`}
                              >
                                {app.name}
                              </CardTitle>
                              <div className="flex flex-shrink-0 flex-col gap-2 sm:ml-3">
                                <Badge
                                  variant="secondary"
                                  className={`font-semibold ${
                                    isEnabled
                                      ? "bg-primary/10 text-primary border-primary/20"
                                      : "bg-muted text-muted-foreground border-muted-foreground/20"
                                  }`}
                                >
                                  {app.slug?.toUpperCase()} v
                                  {app.version || "1.0"}
                                </Badge>
                              </div>
                            </div>
                            <CardDescription
                              className={`text-base leading-relaxed flex-grow ${
                                isEnabled
                                  ? "text-muted-foreground/90"
                                  : "text-muted-foreground/60"
                              }`}
                            >
                              {app.description}
                            </CardDescription>
                          </CardHeader>
                          <CardContent className="pt-0 mt-auto">
                            <div
                              className={`w-full py-2 px-4 rounded-md text-center font-semibold transition-all duration-300 flex items-center justify-center ${
                                isEnabled
                                  ? "bg-primary text-primary-foreground group-hover:shadow-lg"
                                  : "bg-muted text-muted-foreground cursor-not-allowed"
                              }`}
                            >
                              {isEnabled
                                ? "Launch Application"
                                : "Not Available"}
                              <ArrowRight
                                className={`ml-2 h-4 w-4 transition-transform ${
                                  isEnabled ? "group-hover:translate-x-1" : ""
                                }`}
                              />
                            </div>
                          </CardContent>
                        </Card>
                      );

                      return isEnabled ? (
                        <Link
                          key={app.id || app.slug}
                          to={`/applications/${app.slug}`}
                          className="block"
                          onClick={() => {
                            trackRumAction('app_launch', {
                              app_slug: app.slug,
                              app_name: app.name,
                              app_id: app.id,
                            });
                            if (app.id) {
                              localStorage.setItem(
                                "current_application_id",
                                app.id
                              );
                            }
                          }}
                        >
                          {cardContent}
                        </Link>
                      ) : (
                        <div key={app.id || app.slug} className="block">
                          {cardContent}
                        </div>
                      );
                    })}
                  </div>
                )}

              {/* Empty State */}
              {!applicationsLoading &&
                !applicationsError &&
                (!availableApplications ||
                  availableApplications.length === 0) && (
                  <Card className="text-center py-16 bg-background border-2 border-dashed border-muted-foreground/20">
                    <CardContent>
                      <div className="w-16 h-16 mx-auto mb-6 bg-muted/20 rounded-full flex items-center justify-center">
                        <Cloud className="h-8 w-8 text-muted-foreground/50" />
                      </div>
                      <p className="text-lg text-muted-foreground font-medium">
                        No applications are currently available.
                      </p>
                    </CardContent>
                  </Card>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
